/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { css, jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { HorizontalNavProjectBreadcrumbs$key } from '@atlassian/jira-relay/src/__generated__/HorizontalNavProjectBreadcrumbs.graphql';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';

const createProjectUrl = (projectKey: string) => `/browse/${encodeURIComponent(projectKey)}`;

export const HorizontalNavProjectBreadcrumbs = ({
	projectFragment,
	shouldShowProjectName = false,
}: {
	projectFragment: HorizontalNavProjectBreadcrumbs$key | null;
	shouldShowProjectName?: boolean;
}): JSX.Element => {
	const projectsTitleGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.projectsTitleGalaxia
		: messages.projectsTitle;
	const project = useFragment(
		graphql`
			fragment HorizontalNavProjectBreadcrumbs on JiraProject {
				key
				name
			}
		`,
		projectFragment,
	);

	const { formatMessage } = useIntl();

	return (
		<div css={breadcrumbStyles}>
			<Breadcrumbs>
				<BreadcrumbsItem href="/jira/projects" text={formatMessage(projectsTitleGalaxia)} />
				{shouldShowProjectName && project && (
					<BreadcrumbsItem href={createProjectUrl(project?.key)} text={project?.name} />
				)}
			</Breadcrumbs>
		</div>
	);
};

const breadcrumbStyles = css({
	minHeight: '24px',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-text-subtle': Tokens.COLOR_TEXT_SUBTLE,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-text-subtlest': Tokens.COLOR_TEXT_SUBTLEST,
});
